import { createClient } from '@layerzerolabs/scan-client'
import { NetworkType } from 'abis/set/sets'
import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import { OmniTransactionService } from 'sdk/omni/omni-transaction-service/service'
import {
  type Transaction,
  isPending,
  parseReceiptStatus,
  storedTransactionsAtom,
  updateCrossChainTransaction,
  updateSingleChainTransaction,
  useRefresh,
  useTransactionService,
} from 'services'
import { useStore } from 'services/store'
import { viemPublicClients } from 'services/wallet/chains'
import type { Address } from 'viem'
import { useAccount } from 'wagmi'

const apiUrlByChainId = {
  [NetworkType.Mainnet]: 'mainnet',
  [NetworkType.Avalanche]: 'avalanche',
  [NetworkType.Arbitrum]: 'arbitrum',
  [NetworkType.Base]: 'base',
}

export function TransactionUpdater(): null {
  const { isDarkMode, blockNumberInfo } = useStore()
  const transactions = useAtomValue(storedTransactionsAtom)
  const { address, chain } = useAccount()
  const { updateTransaction } = useTransactionService()
  const { fastRefresh } = useRefresh()
  const transactionClient = new OmniTransactionService(createClient('mainnet'))

  useEffect(() => {
    async function updateTransactionsStatuses(txs: Transaction[]) {
      const activeTransactions: Transaction[] = txs.filter(isPending)
      const now = Date.now()

      if (activeTransactions.length) {
        activeTransactions.map(async (tx: Transaction) => {
          let transaction = tx
          const isExpired = now > transaction.addedTime + 300 * 1000 // 5 minutes
          if (isExpired) {
            transaction.status = parseReceiptStatus('reverted')
          } else {
            if (tx.replacementHash) {
              const homeTx = await viemPublicClients[tx.chainId].getTransactionReceipt({
                hash: tx.replacementHash as Address,
              })
              transaction.status = parseReceiptStatus(homeTx.status)
            } else {
              if (tx.isSingleChain && !tx.isUserContract) {
                transaction = await updateSingleChainTransaction(tx, isDarkMode, blockNumberInfo)
              } else if (tx.isUserContract) {
                const responseTx = await fetch(
                  `https://safe-transaction-${apiUrlByChainId[tx.chainId] ?? 'mainnet'}.safe.global/api/v2/multisig-transactions/${transaction.hash}/`,
                )
                const multisigTx = await responseTx.json()

                if (!multisigTx.transactionHash) {
                  const nonce = multisigTx.nonce
                  const responseTxs = await fetch(
                    `https://safe-transaction-${apiUrlByChainId[tx.chainId] ?? 'mainnet'}.safe.global/api/v2/safes/${transaction.account}/all-transactions/?limit=3`,
                  )
                  const multisigTxs = await responseTxs.json()
                  const updatedTx = multisigTxs.results.find((el) => el.nonce === nonce) // for the case if transaction was rejected or rewritten

                  if (updatedTx && updatedTx.safeTxHash !== transaction.hash) {
                    transaction.hash = updatedTx.safeTxHash
                    transaction.isRejected = true
                  }
                } else {
                  transaction.replacementHash = multisigTx.transactionHash
                }
              } else {
                transaction = await updateCrossChainTransaction(tx, isDarkMode, transactionClient)
              }
            }
          }
          updateTransaction(transaction)
        })
      }
    }
    updateTransactionsStatuses(transactions)
  }, [fastRefresh, address, chain, transactions])
  return null
}
